import dynamic from "next/dynamic";
import { Disclosure } from "@headlessui/react";
import { MinusIcon, PlusIcon } from "@heroicons/react/24/solid";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { BLOCKS, MARKS } from "@contentful/rich-text-types";
import MD from "react-markdown";

const Grid = dynamic(() => import("@/components/grid"));

import { richContentOptions } from "@/components/content-options";
import Contact from "@/components/forms/contact";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const Heading = (node, children) => {
  return <h6 className="text-sm font-medium text-brand-600">{children}</h6>;
};

const Paragraph = (node, children) => {
  const isEmptyChildren = children?.toString().trim() === "";
  if (isEmptyChildren) return null;
  return <p className="mt-3 text-sm md:text-base text-gray-500">{children}</p>;
};

const Bold = (text) => {
  return <strong className="font-medium text-black">{text}</strong>;
};

const options = {
  ...richContentOptions,
  renderMark: {
    ...richContentOptions.renderMark,
    [MARKS.BOLD]: Bold,
  },
  renderNode: {
    ...richContentOptions.renderNode,
    [BLOCKS.HEADING_6]: Heading,
    [BLOCKS.PARAGRAPH]: Paragraph,
    [BLOCKS.EMBEDDED_ASSET]: (node) => {
      console.log("NODE", node);
      const { title, file } = node.data.target.fields;
      return (
        <div className="md:float-left w-1/3 mr-8 rounded-lg overflow-hidden mb-6">
          <img
            src={`https:${file.url}`}
            alt={title}
            className="w-full h-full object-cover m-0"
          />
        </div>
      );
    },
  },
};

export default function Accordion({
  fields,
  sys,
  includes,
  addItem = null,
  removeItem = null,
  ...rest
}) {
  if (fields.options && fields.options.card) {
    return (
      <>
        <div className="bg-slate-100">
          <div className="pt-6 sm:pt-8 lg:pt-12">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-6">
              <div className="text-center prose lg:prose-xl mx-auto">
                {fields.title && (
                  <h3 className="text-2xl md:text-4xl tracking-tight font-extrabold sm:text-5xl">
                    {fields.title}
                  </h3>
                )}
                {fields.description && <MD>{fields.description}</MD>}
              </div>
            </div>
          </div>
          <div className="py-8 bg-slate-100 sm:mt-4">
            <div className="relative">
              <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-6">
                <div className="max-w-lg mx-auto rounded-lg shadow-lg overflow-hidden lg:max-w-none flex flex-col lg:flex-row">
                  <div className="bg-white w-full pb-4 mx-auto px-4 sm:max-w-3xl sm:px-6 lg:max-w-7xl lg:px-8">
                    <div className="divide-y">
                      {fields.items.map((item, index) => {
                        return (
                          <Disclosure as="div" key={item.sys.id}>
                            {({ open }) => (
                              <>
                                <h5>
                                  <Disclosure.Button className="group relative w-full py-6 flex justify-between items-center text-left">
                                    <span
                                      className={classNames(
                                        open
                                          ? "text-brand-600"
                                          : "text-gray-900",
                                        "font-semibold text-sm md:text-lg"
                                      )}
                                    >
                                      <span className="inline-block mr-4 font-semibold">
                                        {index + 1}.
                                      </span>
                                      {item.fields.title}
                                    </span>
                                    <span className="ml-6 flex items-center">
                                      {open ? (
                                        <MinusIcon
                                          className="block h-4 w-4 text-brand-400 group-hover:text-brand-500"
                                          aria-hidden="true"
                                        />
                                      ) : (
                                        <PlusIcon
                                          className="block h-4 w-4 text-gray-400 group-hover:text-gray-500"
                                          aria-hidden="true"
                                        />
                                      )}
                                    </span>
                                  </Disclosure.Button>
                                </h5>
                                {item.sys &&
                                item.sys.contentType &&
                                item.sys.contentType.sys &&
                                item.sys.contentType.sys.id ===
                                  "contentReferenceGrid" ? (
                                  <div className="border-t divide-y divide-gray-200">
                                    <Disclosure.Panel
                                      as="div"
                                      unmount={false}
                                      // className="py-6 prose prose-sm md:prose-md lg:prose-lg"
                                      className="sm:max-w-xl sm:mx-auto sm:py-6 md:max-w-none md:mx-0 md:py-0"
                                    >
                                      <Grid
                                        {...item}
                                        plain
                                        addItem={addItem}
                                        removeItem={removeItem}
                                      />
                                    </Disclosure.Panel>
                                  </div>
                                ) : (
                                  <Disclosure.Panel
                                    as="div"
                                    unmount={false}
                                    className="pb-4 prose prose-sm md:prose-md lg:prose-md w-full max-w-full"
                                  >
                                    {documentToReactComponents(
                                      item.fields.content,
                                      options
                                    )}
                                  </Disclosure.Panel>
                                )}
                              </>
                            )}
                          </Disclosure>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {rest.primary &&
        rest.primary.fields &&
        rest.primary.fields.url === "/franchise-with-us" ? (
          <div className="mt-12 mx-auto max-w-xl text-center">
            <h4 className="mt-1 block text-2xl md:text-4xl tracking-tight font-extrabold sm:text-5xl xl:text-6xl">
              Ready to Take the Next Step?
            </h4>
            <div className="mt-3 text-lg text-gray-600 space-y-4 mb-12">
              <p>
                Are you ready to transform your passion for beauty into a
                thriving business? Do you aspire to lead and inspire the next
                generation of beauty professionals?
              </p>
              <p>
                If the answer is yes, then we want to hear from you. To learn
                more about this exciting franchise opportunity, get in touch
                with us today. Request your information pack, schedule a call,
                or arrange a meeting with our management team.{" "}
                <strong className="font-semibold">
                  This is your chance to be a part of something truly special.
                </strong>
              </p>
              <p>
                Your journey to becoming a Cosmetic College franchisee begins
                here. Click to request more information and take the first step
                towards your future in the beauty education industry.
              </p>
            </div>
            <Contact franchise />
          </div>
        ) : null}
      </>
    );
  }

  return (
    <div
      id="faqs"
      className="w-full pb-16 sm:mt-12 sm:pb-20 lg:pb-28 mx-auto px-4 sm:max-w-3xl sm:px-6 lg:max-w-7xl lg:px-8"
    >
      {fields.title && (
        <h4 className="text-2xl font-extrabold text-gray-900 sm:text-3xl">
          {fields.title}
        </h4>
      )}
      <div>
        {fields.items.map((item) => {
          return (
            <Disclosure as="div" key={item.sys.id}>
              {({ open }) => (
                <>
                  <h5>
                    <Disclosure.Button className="group relative w-full py-6 flex justify-between items-center text-left">
                      <span
                        className={classNames(
                          open ? "text-brand-600" : "text-gray-900",
                          "text-lg font-medium"
                        )}
                      >
                        {item.fields.title}
                      </span>
                      <span className="ml-6 flex items-center">
                        {open ? (
                          <MinusIcon
                            className="block h-6 w-6 text-brand-400 group-hover:text-brand-500"
                            aria-hidden="true"
                          />
                        ) : (
                          <PlusIcon
                            className="block h-6 w-6 text-gray-400 group-hover:text-gray-500"
                            aria-hidden="true"
                          />
                        )}
                      </span>
                    </Disclosure.Button>
                  </h5>
                  {item.sys &&
                  item.sys.contentType &&
                  item.sys.contentType.sys &&
                  item.sys.contentType.sys.id === "contentReferenceGrid" ? (
                    <div className="border-t divide-y divide-gray-200">
                      <Disclosure.Panel
                        as="div"
                        unmount={false}
                        // className="py-6 prose prose-sm md:prose-md lg:prose-lg"
                        className="sm:max-w-xl sm:mx-auto sm:py-6 md:max-w-none md:mx-0 md:py-0"
                      >
                        <Grid
                          {...item}
                          plain
                          addItem={addItem}
                          removeItem={removeItem}
                        />
                      </Disclosure.Panel>
                    </div>
                  ) : (
                    <div className="border-t divide-y divide-gray-200">
                      <Disclosure.Panel
                        as="div"
                        unmount={false}
                        className="py-6 prose prose-sm md:prose-md lg:prose-lg"
                      >
                        {documentToReactComponents(
                          item.fields.content,
                          options
                        )}
                      </Disclosure.Panel>
                    </div>
                  )}
                </>
              )}
            </Disclosure>
          );
        })}
      </div>
    </div>
  );
}
